import BaseAPIClient from './backend';
import type { AssessmentStatus } from './api';
import type { AdminUser, AdminUserPackage, BackendUser } from '../types/user';
import type { UserRole } from '../utils/roles';
import type {
  AdminAssessmentResultsQuery,
  AdminHtsSubmissionResultsQuery,
  AssessmentScoreLabel,
  HtsAnswerClassification,
  HtsAnswerOverrideHistoryKind,
  HtsConsistencyFlagOverrideHistoryKind,
  HtsReadinessDimensionOverrideHistoryKind,
  HtsAnswerValue,
  HtsConsistencyFlagOverrideDecision,
  HtsFlagPriority,
  HtsGroupField,
  HtsReviewNoteTarget,
  HtsReadinessBand,
  HtsReadinessNarrativeSource,
  HtsSubmissionStatus,
  StructureComponentScore,
} from '@evaltick/shared';

export interface PaginationMeta {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface AssessmentUser {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string;
}

export interface AdminAssessment {
  id: string;
  status: AssessmentStatus;
  totalScore: number | null;
  label: AssessmentScoreLabel | null;
  reviewFlagged: boolean;
  createdAt: string;
  completedAt: string | null;
  user: AssessmentUser;
  interviewTemplate: { id: string; name: string };
  /** Null for a normal assessment; an array (possibly empty) means an access-code guest. */
  accessCodeTags: string[] | null;
}

// Takes `object` rather than `Record<string, string | undefined>`: a
// concrete query interface's properties are all optional, and TS won't
// assign such a "weak type" to a type with an index signature — casting once
// here (values are only ever strings on these query shapes) avoids forcing
// every call site to do the same.
const buildResultsListParams = (page: number, limit: number, filters: object): URLSearchParams => {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  for (const [key, value] of Object.entries(filters as Record<string, string | undefined>)) {
    if (value) params.set(key, value);
  }
  return params;
};

export interface PaginatedAssessments {
  data: AdminAssessment[];
  pagination: PaginationMeta;
}

interface DimensionScore {
  key: string;
  // null when the coach marked this dimension Not assessed.
  score: number | null;
  weight: number | null;
  rationale: string;
}

export interface AssessmentResultResponse {
  id: string;
  questionType: string;
  questionText: string | null;
  answer: string;
  rationale: string;
  competencyScore: number | null;
  structureScore: number | null;
  structureRationale: string | null;
  structureComponentScores: StructureComponentScore[] | null;
  overallScore: number | null;
  dimensionScores: DimensionScore[] | null;
  reviewFlagged: boolean;
  reviewReasons: string[];
  /** True when this row wasn't scored by this assessment at all — it's the candidate's latest
   * attempt from an earlier sitting, backfilled because a partial retake didn't redo it. */
  isCarriedOver?: boolean;
  /** Which earlier Assessment this carried-over row came from — only set when isCarriedOver is true. */
  fromAssessmentId?: string;
}

export interface CompetencyBreakdown {
  competencyId: string;
  competencyName: string;
  averageScore: number | null;
  label: AssessmentScoreLabel | null;
  reviewFlagged: boolean;
  responses: AssessmentResultResponse[];
}

/** Every admin action ever taken on an answer, oldest to newest. The original automated classification/flag isn't an entry — it stays in `classification`/`reviewFlagged`. */
export type AdminHtsAnswerOverrideHistoryEntry =
  | {
      kind: typeof HtsAnswerOverrideHistoryKind.Classification;
      classification: HtsAnswerClassification | null;
      note: string | null;
      overriddenAt: string;
      overriddenByName: string;
    }
  | {
      kind: typeof HtsAnswerOverrideHistoryKind.HardGate;
      flagged: boolean | null;
      priority: HtsFlagPriority | null;
      note: string | null;
      overriddenAt: string;
      overriddenByName: string;
    }
  | { kind: typeof HtsAnswerOverrideHistoryKind.Note; note: string; overriddenAt: string; overriddenByName: string };

export interface AdminHtsAnswerDetail {
  questionId: string;
  /** Stable questionnaire order — render questions in that order, not answer-row insertion order. */
  order: number;
  /** 1-based position in the questionnaire; matches the number the guest sees. */
  questionNumber: number;
  section: string;
  questionText: string;
  value: HtsAnswerValue;
  isSensitive: boolean;
  classification: HtsAnswerClassification | null;
  reviewFlagged: boolean;
  flagPriority: HtsFlagPriority | null;
  reviewReasons: string[];
  followUpRound: number;
  followUpHistory: { question: string; answer: string | null }[];
  /** When the answer row was created — dates the automated classification/flag shown as the first history row. */
  createdAt: string;
  /** Section B suitability topic — null for every non-suitability question. */
  classificationTopic: string | null;
  /** Readiness pillar(s) this question's own `readinessPillars` names — empty for suitability/hard-gate/informational questions. */
  readinessPillars: string[];
  /** True when this question is hard-gated — mutually exclusive with a non-null `classificationTopic`. */
  isHardGate: boolean;
  /** Non-null only for a REPEATABLE_GROUP question — the sub-field definitions needed to render `value`'s compiled JSON as labeled rows instead of raw JSON. */
  groupFields: HtsGroupField[] | null;
  /** Admin override — suitability rows only. Null = untouched. */
  overrideClassification: HtsAnswerClassification | null;
  /** Admin override — hard-gate rows only. Null = untouched. */
  overrideFlagged: boolean | null;
  /** Admin override — hard-gate rows only. Null = untouched. */
  overridePriority: HtsFlagPriority | null;
  /** The latest override or standalone note on this row, whichever path was used. */
  overrideNote: string | null;
  overriddenAt: string | null;
  overriddenByName: string | null;
  /** Every admin override/note action ever taken on this answer, oldest to newest. */
  overrideHistory: AdminHtsAnswerOverrideHistoryEntry[];
  /** The admin's follow-up and the guest's reply (null until answered). Null if none. */
  adminFollowUp: { question: string; answer: string | null } | null;
}

/** Every admin action ever taken on a consistency flag, oldest to newest. The flag's own detected contradiction isn't an entry — it stays in `note`. */
export type AdminHtsConsistencyFlagOverrideHistoryEntry =
  | {
      kind: typeof HtsConsistencyFlagOverrideHistoryKind.Decision;
      decision: HtsConsistencyFlagOverrideDecision | null;
      note: string | null;
      overriddenAt: string;
      overriddenByName: string;
    }
  | { kind: typeof HtsConsistencyFlagOverrideHistoryKind.Note; note: string; overriddenAt: string; overriddenByName: string };

export interface AdminHtsConsistencyFlagDetail {
  id: string;
  questionIds: string[];
  note: string;
  createdAt: string;
  /** Admin resolution. Null = untouched, which still counts as unresolved. */
  overrideDecision: HtsConsistencyFlagOverrideDecision | null;
  overrideNote: string | null;
  overriddenAt: string | null;
  overriddenByName: string | null;
  /** Every admin override/note action ever taken on this flag, oldest to newest. */
  overrideHistory: AdminHtsConsistencyFlagOverrideHistoryEntry[];
}

/** Every admin action ever taken on a dimension's score, oldest to newest. The automated score isn't an entry — it stays in `score`. */
export type AdminHtsReadinessDimensionOverrideHistoryEntry =
  | {
      kind: typeof HtsReadinessDimensionOverrideHistoryKind.Score;
      score: number | null;
      note: string | null;
      overriddenAt: string;
      overriddenByName: string;
    }
  | { kind: typeof HtsReadinessDimensionOverrideHistoryKind.Note; note: string; overriddenAt: string; overriddenByName: string };

export interface AdminHtsReadinessDimensionScore {
  key: string;
  label: string;
  weight: number;
  rationale: string;
  /** The automated score. Never rewritten by an override — the current value is `overrideScore ?? score`. */
  score: number;
  /** Admin override. Absent = untouched, so the automated `score` stands. */
  overrideScore?: number;
  /** Current note + attribution, shared by a real score override or a standalone note. */
  note?: string;
  overriddenAt?: string;
  overriddenByName?: string;
  /** Every admin override/note action ever taken on this dimension, oldest to newest. */
  overrideHistory: AdminHtsReadinessDimensionOverrideHistoryEntry[];
}

export interface AdminHtsReadinessPillarScore {
  key: string;
  label: string;
  pillarScore: number;
  rationale: string;
  dimensions: AdminHtsReadinessDimensionScore[];
}

export interface AdminHtsReadinessNarrative {
  coachSummary: string;
  candidateSummary: { strengths: string; priorities: string; bottomLine: string };
}

/** One narrative version, oldest to newest — distinguishes a model redraft from an admin's hand-edit. */
export interface AdminHtsReadinessNarrativeHistoryEntry {
  narrative: AdminHtsReadinessNarrative | null;
  source: HtsReadinessNarrativeSource;
  recordedAt: string;
  triggeredByName: string;
}

export interface AdminHtsReadinessProfileDetail {
  compositeScore: number;
  band: HtsReadinessBand;
  presentPillars: string[];
  missingPillars: string[];
  provisional: boolean;
  /** One entry per mandatory-review trigger that fired, plus the standalone unresolved-consistency-flag note — rendered as the "Withheld from the candidate" list. */
  provisionalReasons: string[];
  /** True while a submit-time scoring failure is unresolved — Release won't stamp until the admin acknowledges it. */
  scoringFailureUnresolved: boolean;
  computedAt: string;
  detail:
    | {
        pillars: AdminHtsReadinessPillarScore[];
        /** Answers the scorer set aside as not credible. */
        credibilityConcerns: { questionId: string; note: string }[];
        narrative: AdminHtsReadinessNarrative | null;
        narrativeHistory: AdminHtsReadinessNarrativeHistoryEntry[];
      }
    | null;
  /** Sticky — set true the first time the triggers fired. Gates only the candidate's *first* view. */
  reviewRequired: boolean;
  releasedAt: string | null;
  releasedByName: string | null;
  /** Never shown to the candidate. */
  adminNote: string | null;
  adminNoteUpdatedAt: string | null;
  adminNoteUpdatedByName: string | null;
}

export interface AdminHtsSubmissionDetail {
  id: string;
  status: HtsSubmissionStatus;
  submittedAt: string | null;
  user: AssessmentUser;
  interviewTemplateName: string;
  answers: AdminHtsAnswerDetail[];
  consistencyFlags: AdminHtsConsistencyFlagDetail[];
  perfectPersonProbe:
    | {
        question: string;
        answer: string;
        /** Always a plain note — the probe has no HtsAnswer to override. */
        note: string | null;
        noteUpdatedAt: string | null;
        noteUpdatedByName: string | null;
        noteHistory: AdminHtsAnswerOverrideHistoryEntry[];
      }
    | null;
  readinessProfile: AdminHtsReadinessProfileDetail | null;
  /** Null for a normal submission; an array (possibly empty) means an access-code guest. */
  accessCodeTags: string[] | null;
  isGuest: boolean;
  /** Null until sent. "No follow-ups needed" has `answeredAt === sentAt` and no follow-up on any answer. */
  adminFollowUps: { sentAt: string; sentByName: string | null; answeredAt: string | null } | null;
  /** When this guest can no longer log back in, past or future; null for a non-guest. */
  guestLoginEndsAt: string | null;
}

export interface AdminHtsSubmissionListItem {
  submissionId: string;
  interviewTemplateId: string;
  user: AssessmentUser;
  interviewTemplateName: string;
  submittedAt: string | null;
  compositeScore: number | null;
  band: HtsReadinessBand | null;
  flaggedAnswerCount: number;
  /** Null for a normal submission; an array (possibly empty) means an access-code guest. */
  accessCodeTags: string[] | null;
  reviewRequired: boolean;
  releasedAt: string | null;
  releasedByName: string | null;
}

export interface PaginatedHtsSubmissions {
  data: AdminHtsSubmissionListItem[];
  pagination: PaginationMeta;
}

// ── Admin review batch ───────────────────────────────────────────────────────
// Every override below carries a real, valid value and a note — there is no
// null/"clear" sentinel. Reverting a question or dimension to its original
// automated value means asserting that value explicitly, same as any other
// change (see htsAdminReviewCore.ts's HtsAdminReviewBatch doc comment).
export interface AdminHtsReviewAnswerOverride {
  questionId: string;
  classification: HtsAnswerClassification;
  note: string;
}
export interface AdminHtsReviewHardGateOverride {
  questionId: string;
  flagged: boolean;
  priority: HtsFlagPriority | null;
  note: string;
}
export interface AdminHtsReviewConsistencyFlagOverride {
  flagId: string;
  decision: HtsConsistencyFlagOverrideDecision;
  note: string;
}
export interface AdminHtsReviewDimensionOverride {
  pillarKey: string;
  dimensionKey: string;
  score: number;
  note: string;
}
export interface AdminHtsReviewItemNote {
  target: HtsReviewNoteTarget;
  /** questionId / flagId / `${pillarKey}:${dimensionKey}` — matching whichever of the four arrays above the target uses. `probe` is always HTS_PERFECT_PERSON_PROBE_ID. */
  id: string;
  note: string;
}

/** Everything staged during one visit to the review page, committed in a single request. */
export interface AdminHtsReviewBatch {
  answerOverrides: AdminHtsReviewAnswerOverride[];
  hardGateOverrides: AdminHtsReviewHardGateOverride[];
  consistencyFlagOverrides: AdminHtsReviewConsistencyFlagOverride[];
  dimensionOverrides: AdminHtsReviewDimensionOverride[];
  itemNotes: AdminHtsReviewItemNote[];
  /** The admin's explicit sign-off that a submit-time scoring failure has been dealt with — required to release while `scoringFailureUnresolved` is true. */
  scoringFailureAcknowledged?: boolean;
}

/** What `previewHtsReadinessNarrative` returns — nothing is persisted by that call. */
export interface AdminHtsReadinessNarrativePreview {
  /** True when the batch includes a dimension-score change — decides whether `narrative` is a fresh model draft or the existing, unchanged one. */
  needsNarrativeRedraft: boolean;
  narrative: AdminHtsReadinessNarrative | null;
  /** True when a redraft was called for but the model call failed — `narrative` is the existing text, describing the pre-override scores, so it can't be released as a redraft. */
  redraftFailed: boolean;
}

export interface AssessmentResults {
  id: string;
  status: string;
  createdAt: string;
  completedAt: string | null;
  totalScore: number | null;
  label: AssessmentScoreLabel | null;
  user: AssessmentUser;
  interviewTemplate: { id: string; name: string };
  competencyBreakdown: CompetencyBreakdown[];
  scoreBlendConfig: {
    competencyWeight: number;
    structureWeight: number;
    maxStructureScore: number;
  };
  /** AI-generated session write-up — null until the candidate's own results page has generated it. */
  summaryNarrative: string | null;
  /** General reviewer note — never shown to the candidate. */
  adminNote: string | null;
  adminNoteUpdatedAt: string | null;
  adminNoteUpdatedByName: string | null;
  /** Null for a normal assessment; an array (possibly empty) means an access-code guest. */
  accessCodeTags: string[] | null;
}

export type AssessmentAdminNote = Pick<AssessmentResults, 'adminNote' | 'adminNoteUpdatedAt' | 'adminNoteUpdatedByName'>;

export interface PaginatedUsers {
  data: AdminUser[];
  pagination: PaginationMeta;
}

export type TraceStatus = 'success' | 'error' | 'running';

export interface AdminTraceListItem {
  traceId: string;
  name: string;
  spanType: string;
  status: TraceStatus;
  entityName: string | null;
  startedAt: string;
  endedAt: string | null;
  error: unknown;
  competencyId?: string;
  questionType?: string;
  phase?: string;
  threadId?: string;
  assessmentId?: string;
  /** 'voice' for a live realtime voice session's span; absent for an ordinary text-mode trace. */
  mode?: string;
  /** Azure's call identifier; voice traces only. */
  callId?: string;
  /** 'natural' | 'length_cap'; voice traces only, set once the session ends. */
  endReason?: string;
}

export interface PaginatedTraces {
  data: AdminTraceListItem[];
  pagination: PaginationMeta;
}

export interface AdminTraceSpan {
  spanId: string;
  parentSpanId: string | null;
  name: string;
  spanType: string;
  startedAt: string;
  endedAt: string | null;
  error: unknown;
  entityType: string | null;
  entityName: string | null;
}

export interface AdminTraceDetail {
  traceId: string;
  competencyId?: string;
  questionType?: string;
  phase?: string;
  questionText?: string;
  mode?: string;
  /** Azure's call identifier; voice traces only. */
  callId?: string;
  /** 'natural' | 'length_cap'; voice traces only, set once the session ends. */
  endReason?: string;
  spans: AdminTraceSpan[];
}

export interface AdminTraceSpanDetail {
  input: unknown;
  output: unknown;
  error: unknown;
  attributes: Record<string, unknown> | null;
}

export interface ResultsExportSelection {
  assessmentIds: string[];
  htsSubmissionIds: string[];
}

export interface ResultsExportParts {
  competency: boolean;
  htsReadiness: boolean;
  htsAnswers: boolean;
}

/** One PDF's candidate (a sitting and its linked HTS submission count once). */
export interface ResultsExportCandidate {
  name: string;
  htsAttached: boolean;
  competencyCompleted: boolean;
  htsCompleted: boolean;
}

export type ResultsExportStatus = 'queued' | 'processing' | 'done' | 'failed';

export interface ResultsExportState {
  status: ResultsExportStatus;
  progress: { done: number; total: number };
  /** Only once `status` is done. */
  filename?: string;
  error?: string;
}

export class AdminService extends BaseAPIClient {
  async getUsers(
    page: number,
    limit: number,
    search?: string,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc',
  ): Promise<PaginatedUsers> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    if (sortBy) params.set('sortBy', sortBy);
    if (sortOrder) params.set('sortOrder', sortOrder);
    return this.request<PaginatedUsers>(() => this.client.get(`/admin/users?${params}`));
  }

  async inviteUser(
    firstName: string | null,
    lastName: string | null,
    email: string,
    role: UserRole,
    appUrl: string,
  ): Promise<AdminUser> {
    return this.request<AdminUser>(
      () => this.client.post('/admin/users', { firstName, lastName, email, role, appUrl }),
    );
  }

  async updateUser(
    userId: number,
    data: Partial<Pick<BackendUser, 'firstName' | 'lastName' | 'email' | 'role'>>,
  ): Promise<AdminUser> {
    return this.request<AdminUser>(
      () => this.client.patch(`/admin/users/${userId}`, data),
    );
  }

  async resendInvite(userId: number, appUrl: string): Promise<void> {
    return this.request<void>(
      () => this.client.post(`/admin/users/${userId}/resend-invite`, { appUrl }),
    );
  }

  async grantPackage(userId: number, packageId: string): Promise<{ balance: number }> {
    return this.request<{ balance: number }>(
      () => this.client.post(`/admin/users/${userId}/packages`, { packageId }),
    );
  }

  async getUserPackages(userId: number): Promise<{ ownedPackages: AdminUserPackage[]; balance: number }> {
    return this.request<{ ownedPackages: AdminUserPackage[]; balance: number }>(
      () => this.client.get(`/admin/users/${userId}/packages`),
    );
  }

  async revokePackage(
    userId: number,
    packageId: string,
  ): Promise<{ revokedCount: number; creditsRevoked: number; balance: number }> {
    return this.request<{ revokedCount: number; creditsRevoked: number; balance: number }>(
      () => this.client.post(
        `/admin/users/${userId}/packages/${encodeURIComponent(packageId)}/revoke`,
      ),
    );
  }

  async addCredits(userId: number, amount: number): Promise<{ balance: number }> {
    return this.request<{ balance: number }>(
      () => this.client.post(`/admin/users/${userId}/credits`, { amount }),
    );
  }

  async listAssessments(
    page: number,
    limit: number,
    filters: AdminAssessmentResultsQuery = {},
  ): Promise<PaginatedAssessments> {
    const params = buildResultsListParams(page, limit, filters);
    return this.request<PaginatedAssessments>(() => this.client.get(`/admin/assessments?${params}`));
  }

  async getAssessmentResults(assessmentId: string): Promise<AssessmentResults> {
    return this.request<AssessmentResults>(
      () => this.client.get(`/admin/assessments/${assessmentId}/results`),
    );
  }

  async listHtsSubmissions(
    page: number,
    limit: number,
    filters: AdminHtsSubmissionResultsQuery = {},
  ): Promise<PaginatedHtsSubmissions> {
    const params = buildResultsListParams(page, limit, filters);
    return this.request<PaginatedHtsSubmissions>(() => this.client.get(`/admin/hts-submissions?${params}`));
  }

  async getHtsSubmission(submissionId: string): Promise<AdminHtsSubmissionDetail> {
    return this.request<AdminHtsSubmissionDetail>(
      () => this.client.get(`/admin/hts-submissions/${submissionId}`),
    );
  }

  /** Sends the follow-up round; an empty list confirms "no follow-ups needed". Returns the refreshed detail. */
  async sendHtsAdminFollowUps(
    submissionId: string,
    followUps: { questionId: string; question: string }[],
  ): Promise<AdminHtsSubmissionDetail> {
    return this.request<AdminHtsSubmissionDetail>(
      () => this.client.post(`/admin/hts-submissions/${submissionId}/admin-follow-ups`, { followUps }),
    );
  }

  /** Previews Save/Release's narrative step for a staged batch without persisting anything; `narrative` is unchanged when `needsNarrativeRedraft` is false. */
  async previewHtsReadinessNarrative(
    submissionId: string,
    batch: AdminHtsReviewBatch,
  ): Promise<AdminHtsReadinessNarrativePreview> {
    return this.request<AdminHtsReadinessNarrativePreview>(
      () => this.client.post(`/admin/hts-submissions/${submissionId}/review/preview-narrative`, batch),
    );
  }

  /** Commits a staged batch plus the approved narrative. `finalNarrative` is always sent (what gets released); `generatedNarrative` only when the batch redrafted it. */
  async saveHtsAdminReview(
    submissionId: string,
    batch: AdminHtsReviewBatch,
    finalNarrative: AdminHtsReadinessNarrative | null,
    generatedNarrative?: AdminHtsReadinessNarrative | null,
  ): Promise<AdminHtsSubmissionDetail> {
    return this.request<AdminHtsSubmissionDetail>(
      () => this.client.post(`/admin/hts-submissions/${submissionId}/review`, { ...batch, finalNarrative, generatedNarrative }),
    );
  }

  /** The general reviewer note — saves immediately, independent of the staged batch above. Returns the refreshed detail so the page can render the note's new attribution. */
  async updateHtsAdminNote(submissionId: string, note: string): Promise<AdminHtsSubmissionDetail> {
    return this.request<AdminHtsSubmissionDetail>(
      () => this.client.patch(`/admin/hts-submissions/${submissionId}/admin-note`, { note }),
    );
  }

  /** Saves the reviewer note on a completed sitting. Returns only the note fields, to merge into the page's results. */
  async updateAssessmentAdminNote(assessmentId: string, note: string): Promise<AssessmentAdminNote> {
    return this.request<AssessmentAdminNote>(
      () => this.client.patch(`/admin/assessments/${assessmentId}/admin-note`, { note }),
    );
  }

  /** Which parts each selected candidate has finished. */
  async getResultsExportAvailability(selection: ResultsExportSelection): Promise<ResultsExportCandidate[]> {
    const { candidates } = await this.request<{ candidates: ResultsExportCandidate[] }>(
      () => this.client.post('/admin/results-exports/availability', selection),
    );
    return candidates;
  }

  async createResultsExport(selection: ResultsExportSelection, parts: ResultsExportParts): Promise<string> {
    const { exportId } = await this.request<{ exportId: string }>(
      // So PDF dates match the app's local-time dates.
      () => this.client.post('/admin/results-exports', {
        ...selection,
        parts,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }),
    );
    return exportId;
  }

  async getResultsExport(exportId: string): Promise<ResultsExportState> {
    return this.request<ResultsExportState>(() => this.client.get(`/admin/results-exports/${exportId}`));
  }

  /** Uses the raw fetchClient since APIClient parses every body as JSON. A 404 means it expired. */
  async downloadResultsExport(exportId: string): Promise<Blob> {
    const response = await this.fetchClient(`/admin/results-exports/${exportId}/download`);
    if (!response.ok) throw new Error(response.status === 404 ? 'Export expired — export again' : `HTTP ${response.status}`);
    return response.blob();
  }

  async listTraces(
    page: number,
    limit: number,
    filters?: { status?: TraceStatus; entityName?: string },
  ): Promise<PaginatedTraces> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters?.status) params.set('status', filters.status);
    if (filters?.entityName) params.set('entityName', filters.entityName);
    return this.request<PaginatedTraces>(() => this.client.get(`/admin/traces?${params}`));
  }

  async getTraceDetail(traceId: string): Promise<AdminTraceDetail> {
    return this.request<AdminTraceDetail>(() => this.client.get(`/admin/traces/${traceId}`));
  }

  async getTraceSpan(traceId: string, spanId: string): Promise<AdminTraceSpanDetail> {
    return this.request<AdminTraceSpanDetail>(
      () => this.client.get(`/admin/traces/${traceId}/spans/${spanId}`),
    );
  }
}
